import { AppState, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from './api';

// Firebase Analytics wrapper. Only used on Android — the iOS native module
// is deliberately excluded from autolinking (see react-native.config.js)
// because we don't collect analytics on iOS. Web is also a no-op.
// Server-side events go to tt-api's /analytics/log-event on every platform.

let _analytics: any = null;
let _analyticsAttempted = false;

function getAnalytics() {
  if (_analytics) return _analytics;
  if (_analyticsAttempted) return null; // already tried & failed — don't retry
  if (Platform.OS !== 'android') return null;

  try {
    // Dynamic require keeps the native-only module out of web bundles.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@react-native-firebase/analytics');
    _analytics = typeof mod === 'function' ? mod() : mod.default ? mod.default() : mod;
    return _analytics;
  } catch {
    // Native module not available (e.g. iOS where autolinking is disabled)
    _analyticsAttempted = true;
    return null;
  }
}

/** Log a custom event (name: 1-40 alphanumeric/underscore, not a reserved name).
 *
 * Fires to two places:
 * 1. Firebase Analytics (Android only, see above)
 * 2. tt-api /analytics/log-event (all platforms; server links user via stored
 *    session token). Never throws, never blocks.
 */
export function logEvent(name: string, params: Record<string, any> = {}): Promise<void> {
  logServerEvent(name, params);
  const analytics = getAnalytics();
  return analytics ? analytics.logEvent(name, params) : Promise.resolve();
}

/** Best-effort fire-and-forget post to the internal analytics DB.
 *
 * Gated on the same consent toggle as Firebase collection
 * (hooks/useAnalyticsConsent.ts — 'analytics_consent_enabled').
 */
export async function logServerEvent(name: string, params: Record<string, any> = {}): Promise<void> {
  try {
    // ponytail: read consent per event — one AsyncStorage hit, always fresh.
    const stored = await AsyncStorage.getItem('analytics_consent_enabled');
    const enabled = stored === null ? true : stored === 'true'; // mirrors DEFAULT_CONSENT_ENABLED (testing gate)
    if (!enabled) return;
    const raw = await AsyncStorage.getItem('session');
    const token = raw ? JSON.parse(raw)?.accessToken : null;
    await fetch(`${API_URL}/analytics/log-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        name,
        ...params,
        platform: Platform.OS === 'web' ? 'web' : Platform.OS,
      }),
    });
  } catch {
    // analytics must never break the app
  }
}

/** Log a screen view. Call on route focus for per-screen analytics. */
export function logScreenView(screenName: string, screenClass?: string): Promise<void> {
  const analytics = getAnalytics();
  if (!analytics) return Promise.resolve();
  return analytics.logEvent('screen_view', {
    screen_name: screenName,
    screen_class: screenClass ?? screenName,
  });
}

let _lifecycleStarted = false;
/** Track app opens/foregrounds + backgrounds as app_open / app_background.
 *
 * These two events are the "opened the app" signal behind MAU / WAU /
 * retention / churn / session-length metrics in the internal analytics DB.
 * Call once from the root layout.
 */
export function initLifecycleTracking(): void {
  if (_lifecycleStarted) return;
  _lifecycleStarted = true;
  try {
    AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        logEvent('app_open');
      } else if (state === 'background') {
        logServerEvent('app_background');
      }
    });
  } catch {
    // analytics must never break the app
  }
}

/** Enable/disable collection (e.g. honour a user privacy toggle). */
export function setAnalyticsCollectionEnabled(enabled: boolean): Promise<void> {
  const analytics = getAnalytics();
  return analytics ? analytics.setAnalyticsCollectionEnabled(enabled) : Promise.resolve();
}

/** Set the current user id for cross-event attribution; pass null to clear. */
export function setUserId(id: string | null): Promise<void> {
  const analytics = getAnalytics();
  return analytics ? analytics.setUserId(id) : Promise.resolve();
}